/**
 * ExamFlow Web API Client
 *
 * With Casdoor as the identity provider, the access token is managed
 * by oidc-client-ts. This file provides a thin wrapper to extract
 * the token and configure the API client.
 */
import {
  createAuthService,
  createBankService,
  createQuestionService,
  createExamService,
  createLearningService,
  createMistakeService,
  createSyncService,
  createAIService,
  createLicenseService,
  createPlatformService,
  createRequest,
  ClientConfig,
} from '@examflow/api';

/**
 * Re-export the shared ApiError so page catch blocks can detect quota
 * failures (HTTP 402) emitted by quotaGuard and render UpgradePrompt.
 * `ApiError.body` carries the parsed JSON envelope which, for 402
 * quota responses, contains `{ error, metric, resetAt }`.
 */
export { ApiError } from '@examflow/api';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

/**
 * Returns the OIDC access token via a global bridge function.
 * Set by AuthProvider using oidc-client-ts's UserManager.
 */
let _getAccessToken: (() => Promise<string | null>) | null = null;

export function setOidcAccessTokenGetter(fn: () => Promise<string | null>) {
  _getAccessToken = fn;
}

async function getAccessToken(): Promise<string | null> {
  if (_getAccessToken) {
    return _getAccessToken();
  }
  return null;
}

const handleUnauthorized = () => {
  window.dispatchEvent(new CustomEvent('auth:unauthorized'));
  const publicAuthPaths = ['/', '/redeem'];
  if (!publicAuthPaths.includes(window.location.pathname)) {
    setTimeout(() => {
      const redirect = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/login?redirect=${redirect}`;
    }, 100);
  }
};

const handleEmailNotVerified = () => {
  // Casdoor handles email verification during sign-up.
};

const config: ClientConfig = {
  baseUrl: API_BASE_URL,
  getAccessToken,
  onUnauthorized: handleUnauthorized,
  onEmailNotVerified: handleEmailNotVerified,
};

export const auth = createAuthService(config);
export const banks = createBankService(config);
export const questions = createQuestionService(config);
export const exams = createExamService(config);
export const learning = createLearningService(config);
export const mistakes = createMistakeService(config);
export const sync = createSyncService(config);
export const ai = createAIService(config);
export const licenses = createLicenseService(config);
export const platform = createPlatformService(config);

export type {
  User,
  AuthResponse,
  ActiveEntitlements,
  LicenseAuthResponse,
  LicensePlan,
  RedemptionCode,
  LearnerAccessibleBankScope,
  Question,
  QuestionBank,
  Mistake,
  MistakeQuestion,
  MistakeStats,
  ExamSession,
  ExamDetail,
  AIGenerationSourceMetadata,
  TrainingAttemptRecord,
  TrainingAttemptRecordQuery,
  TrainingAttemptQuestionMetric,
  TrainingAttemptSectionSummary,
  CreateTrainingAttemptRecordData,
  PaginatedResponse,
  PublicPlatformSettings,
  LearnerFeatureGates,
  SystemSettings,
} from '@examflow/api';

export const api = {
  auth,
  banks,
  questions,
  exams,
  learning,
  mistakes,
  sync,
  ai,
  licenses,
  platform,
  get: <T>(endpoint: string, options?: RequestInit) => createRequest<T>(endpoint, config, options),
  post: <T>(endpoint: string, body?: unknown) =>
    createRequest<T>(endpoint, config, {
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
};

export default api;
